import type { InventoryHealthRow } from '@api/replenishment-planning';
import MacWindowModal from '@components/ui/MacWindowModal';
import { Badge, Divider, Group, Stack, Text } from '@mantine/core';
import { Fragment, type FC } from 'react';

import { explosionContributors } from '../../../utils/skuExplosion';

import classes from './ExplosionDetailPanel.module.css';
import type { HealthRow } from './grouping';
import { formatCount } from '@utils/currency';

/**
 * The audit trail behind a broken-down row: which SKUs were folded into it, at what
 * multiplier, and how each derived figure follows from the totals.
 *
 * It exists because break-down changes numbers the operator is about to act on. A row that
 * silently reads 8 instead of 2 is not trustworthy however correct it is — the question
 * "why does this say what it says" has to be answerable in one click, in the same units the
 * grid shows, without anybody re-deriving it by hand.
 *
 * Every figure on the TOTAL line is read straight off the row the grid is rendering rather
 * than re-summed here. Two independent computations of the same total is how a panel and its
 * grid start disagreeing; this way the panel can only ever explain what is actually on screen.
 */

type Props = {
    /** The exploded row whose badge was clicked. Null closes the window. */
    row: HealthRow | null;
    /**
     * The rows as they arrived from the server, before any explosion, keyed by product id.
     * The contributors' own figures are read from here — they no longer exist as rows in the
     * grid, which is the whole reason this panel is needed.
     */
    sourceRows: Map<string, InventoryHealthRow>;
    onClose: () => void;
};

/** Trims trailing zeros so a units column reads 505 rather than 505.00. */
const num = (value: number | null | undefined): string =>
    value == null
        ? '—'
        : formatCount(value.toFixed(2), { maxDecimals: 2 });

type Line = {
    id: string;
    name: string | null;
    role: string;
    /** Null on the row's own line — it is not multiplied by anything. */
    multiplier: number | null;
    onHand: number;
    inTransit: number;
    open: number;
    drr: number;
    /** True when the SKU could not be found in the pre-explosion set. */
    missing?: boolean;
};

const ExplosionDetailPanel: FC<Props> = ({ row, sourceRows, onClose }) => {
    const contributors = row ? explosionContributors(row) : [];

    const own = row ? sourceRows.get(row.productId) : undefined;

    const lines: Line[] = [];
    if (row) {
        // The surviving SKU's own position first, because that is the number the operator
        // knows from the un-exploded view and the one they are reconciling against.
        lines.push({
            id: row.productId,
            name: row.productName,
            role: own ? 'This SKU' : 'This SKU (no stock record)',
            multiplier: null,
            onHand: own?.currentStock ?? 0,
            inTransit: own?.inbound.inTransit ?? 0,
            open: own?.inbound.open ?? 0,
            drr: own?.drr ?? 0,
            missing: !own,
        });

        for (const contributor of contributors) {
            const source = sourceRows.get(contributor.parentId);
            lines.push({
                id: contributor.parentId,
                name: source?.productName ?? null,
                role: 'Broken down into this',
                multiplier: contributor.quantity,
                // Already multiplied, so the column adds up down the page. The multiplier is
                // shown in its own column rather than left implicit in a formula.
                onHand: (source?.currentStock ?? 0) * contributor.quantity,
                inTransit: (source?.inbound.inTransit ?? 0) * contributor.quantity,
                open: (source?.inbound.open ?? 0) * contributor.quantity,
                drr: (source?.drr ?? 0) * contributor.quantity,
                missing: !source,
            });
        }
    }

    const available = row ? row.currentStock + row.inbound.total : 0;
    const leadPlusSafety = row
        ? row.reorderLeadTimeDays + row.reorderSafetyDays
        : 0;

    return (
        <MacWindowModal
            opened={row !== null}
            onClose={onClose}
            title={`Break-down — ${row?.productId ?? ''}`}
        >
            {row && (
                <Stack gap="lg">
                    <Text size="xs" c="dimmed">
                        {contributors.length} SKU(s) broken down into this row. Quantities are
                        shown already multiplied by the mapping quantity, so each column adds
                        up to the total.
                    </Text>

                    <div className={classes.scroll}>
                        <div className={classes.grid}>
                            <div className={classes.columnHeader}>SKU</div>
                            <div className={classes.columnHeader}>Role</div>
                            <div className={`${classes.columnHeader} ${classes.numeric}`}>
                                ×
                            </div>
                            <div className={`${classes.columnHeader} ${classes.numeric}`}>
                                On hand
                            </div>
                            <div className={`${classes.columnHeader} ${classes.numeric}`}>
                                In transit
                            </div>
                            <div className={`${classes.columnHeader} ${classes.numeric}`}>
                                Open
                            </div>
                            <div className={`${classes.columnHeader} ${classes.numeric}`}>
                                DRR
                            </div>

                            {lines.map((line) => (
                                <Fragment key={line.id}>
                                    <Stack gap={0}>
                                        <Text size="xs" fw={500}>
                                            {line.id}
                                        </Text>
                                        {line.name && (
                                            <Text size="10px" c="dimmed" lineClamp={1}>
                                                {line.name}
                                            </Text>
                                        )}
                                    </Stack>
                                    <Text
                                        size="10px"
                                        c={line.missing ? 'orange' : 'dimmed'}
                                    >
                                        {line.role}
                                    </Text>
                                    <Text size="xs" className={classes.numeric}>
                                        {line.multiplier == null
                                            ? '—'
                                            : `× ${num(line.multiplier)}`}
                                    </Text>
                                    <Text size="xs" className={classes.numeric}>
                                        {num(line.onHand)}
                                    </Text>
                                    <Text size="xs" className={classes.numeric}>
                                        {num(line.inTransit)}
                                    </Text>
                                    <Text size="xs" className={classes.numeric}>
                                        {num(line.open)}
                                    </Text>
                                    <Text size="xs" className={classes.numeric}>
                                        {num(line.drr)}
                                    </Text>
                                </Fragment>
                            ))}

                            {/* Read off the row the grid is rendering, not re-summed. */}
                            <Text size="xs" className={classes.totalRow}>
                                Total
                            </Text>
                            <div className={classes.totalRow} />
                            <div className={`${classes.totalRow} ${classes.numeric}`} />
                            <Text
                                size="xs"
                                className={`${classes.totalRow} ${classes.numeric}`}
                            >
                                {num(row.currentStock)}
                            </Text>
                            <Text
                                size="xs"
                                className={`${classes.totalRow} ${classes.numeric}`}
                            >
                                {num(row.inbound.inTransit)}
                            </Text>
                            <Text
                                size="xs"
                                className={`${classes.totalRow} ${classes.numeric}`}
                            >
                                {num(row.inbound.open)}
                            </Text>
                            <Text
                                size="xs"
                                className={`${classes.totalRow} ${classes.numeric}`}
                            >
                                {num(row.drr)}
                            </Text>
                        </div>
                    </div>

                    <Divider />

                    <Stack gap={8}>
                        <Group gap={8} align="baseline">
                            <Text size="13px" fw={600}>
                                Derived from those totals
                            </Text>
                            <Text size="10px" c="dimmed">
                                these are recomputed, never added up
                            </Text>
                        </Group>

                        {/*
                         * Spelled out as arithmetic rather than described in prose. The whole
                         * reason these three cannot be summed across contributors is that they
                         * are functions of the columns above — showing the substitution makes
                         * that visible instead of asking the reader to trust it.
                         */}
                        <Text className={classes.formula}>
                            {`Cover (days)  = on hand ÷ DRR = ${num(row.currentStock)} ÷ ${num(row.drr)} = ${num(row.coverDays)}`}
                        </Text>
                        {/*
                          * The window is named because the rate here is the POLICY one, which
                          * is not the DRR shown in the row above whenever the two differ. A
                          * derivation panel that quietly used a different rate from the one on
                          * screen would be the least trustworthy thing on the page.
                          */}
                        <Text className={classes.formula}>
                            {`Reorder point = ${row.ropWindowDays}d DRR × (lead ${row.reorderLeadTimeDays}d + safety ${row.reorderSafetyDays}d) = ${num(row.ropDrr)} × ${leadPlusSafety} = ${num(row.reorderPoint)}`}
                        </Text>
                        <Text className={classes.formula}>
                            {`Shortfall     = max(0, reorder point − (on hand + inbound)) = max(0, ${num(row.reorderPoint)} − ${num(available)}) = ${num(row.shortfall)}`}
                        </Text>
                    </Stack>

                    {lines.some((line) => line.missing) && (
                        <Group gap={8} align="flex-start" wrap="nowrap">
                            <Badge size="xs" variant="light" color="orange">
                                note
                            </Badge>
                            <Text size="xs" c="dimmed">
                                A SKU marked above has no row in the current scope — it holds
                                no stock record, or it sits outside the filters. Its
                                contribution is counted as zero, which is why the columns can
                                total less than you might expect from the SKU count.
                            </Text>
                        </Group>
                    )}
                </Stack>
            )}
        </MacWindowModal>
    );
};

export default ExplosionDetailPanel;
